import proj4 from 'proj4';

/**
 * proj4@2 bundles few EPSG entries; NAD83 / UTM north (e.g. 26908) is not included.
 * Register EPSG:26907–26922 as NAD83 UTM zones 7N–22N on first use.
 * @param {number} nativeEpsg
 */
function ensureNad83UtmNorthDef(nativeEpsg) {
  if (!Number.isFinite(nativeEpsg)) return;
  if (nativeEpsg < 26907 || nativeEpsg > 26922) return;
  const name = `EPSG:${nativeEpsg}`;
  if (proj4.defs(name)) return;
  const zone = nativeEpsg - 26900;
  proj4.defs(name, `+proj=utm +zone=${zone} +datum=NAD83 +units=m +no_defs`);
}

/** @param {number} nativeEpsg */
function assertFiniteNativeEpsg(nativeEpsg) {
  if (!Number.isFinite(nativeEpsg) || nativeEpsg <= 0) {
    throw new Error(`[rasterOrthoTiles] invalid nativeEpsg: ${nativeEpsg}`);
  }
}

const WM = 20037508.342789244;
const WM_SIZE = 2 * WM;

/**
 * @param {number} minMx
 * @param {number} maxMx
 * @param {number} minMy
 * @param {number} maxMy
 * @param {number} z
 * @returns {{ minTx: number, maxTx: number, minTy: number, maxTy: number, tileCount: number }}
 */
export function webMercatorTileIndexRange(minMx, maxMx, minMy, maxMy, z) {
  const n = 2 ** z;
  const scale = 256 * n;
  const corners = [
    [minMx, minMy],
    [minMx, maxMy],
    [maxMx, minMy],
    [maxMx, maxMy],
  ];
  let minTx = Infinity;
  let maxTx = -Infinity;
  let minTy = Infinity;
  let maxTy = -Infinity;
  for (const [mx, my] of corners) {
    const tx = Math.floor(((mx + WM) / WM_SIZE) * scale / 256);
    const ty = Math.floor(((WM - my) / WM_SIZE) * scale / 256);
    minTx = Math.min(minTx, tx);
    maxTx = Math.max(maxTx, tx);
    minTy = Math.min(minTy, ty);
    maxTy = Math.max(maxTy, ty);
  }
  minTx = Math.max(0, Math.min(minTx, n - 1));
  maxTx = Math.max(0, Math.min(maxTx, n - 1));
  minTy = Math.max(0, Math.min(minTy, n - 1));
  maxTy = Math.max(0, Math.min(maxTy, n - 1));
  const tileCount = (maxTx - minTx + 1) * (maxTy - minTy + 1);
  return { minTx, maxTx, minTy, maxTy, tileCount };
}

/**
 * @param {number} minTx
 * @param {number} maxTx
 * @param {number} minTy
 * @param {number} maxTy
 * @param {number} z
 */
export function stitchBoundsWebMercatorMeters(minTx, maxTx, minTy, maxTy, z) {
  const n = 2 ** z;
  const minMx = (minTx / n) * WM_SIZE - WM;
  const maxMx = ((maxTx + 1) / n) * WM_SIZE - WM;
  const northMy = WM - (minTy / n) * WM_SIZE;
  const southMy = WM - ((maxTy + 1) / n) * WM_SIZE;
  return { minMx, maxMx, northMy, southMy };
}

/**
 * Pick zoom so tile count <= maxTiles (count downward from tileMaxZoom).
 */
export function pickZoomForWebMercatorBBox(minMx, maxMx, minMy, maxMy, options = {}) {
  const maxTiles = options.maxTiles ?? 36;
  const zMin = Number.isFinite(options.tileMinZoom) ? options.tileMinZoom : 0;
  const zMax = Number.isFinite(options.tileMaxZoom) ? options.tileMaxZoom : 22;
  let best = null;
  for (let z = Math.min(zMax, 22); z >= zMin; z--) {
    const r = webMercatorTileIndexRange(minMx, maxMx, minMy, maxMy, z);
    if (r.tileCount <= maxTiles) {
      best = { z, range: r };
      break;
    }
  }
  if (!best) {
    const z = zMin;
    const range = webMercatorTileIndexRange(minMx, maxMx, minMy, maxMy, z);
    return { z, range };
  }
  return best;
}

function logTileFetchError(status, url, text) {
  try {
    if (text && text.trim().startsWith('{')) {
      const j = JSON.parse(text);
      // eslint-disable-next-line no-console
      console.error('[rasterOrthoTiles] tile HTTP', status, url, j.error, j.message, j);
    } else {
      // eslint-disable-next-line no-console
      console.error('[rasterOrthoTiles] tile HTTP', status, url, (text || '').slice(0, 400));
    }
  } catch {
    // eslint-disable-next-line no-console
    console.error('[rasterOrthoTiles] tile HTTP', status, url, (text || '').slice(0, 400));
  }
}

/**
 * @param {object} opts
 * @param {number} opts.z
 * @param {{ minTx: number, maxTx: number, minTy: number, maxTy: number }} opts.range
 * @param {number} opts.assetId
 * @param {(assetId: number, z: number, x: number, y: number) => string} opts.getTileUrl
 * @param {Record<string, string>} [opts.fetchHeaders]
 * @param {number} [opts.concurrency]
 * @param {boolean} [opts.tmsTileY=false] if true, request tile row as TMS: yUrl = (2^z - 1) - ty (slippy layout unchanged)
 * @returns {Promise<{ canvas: HTMLCanvasElement, minMx: number, maxMx: number, northMy: number, southMy: number }|null>}
 */
export async function fetchStitchWebMercatorTiles(opts) {
  const {
    z,
    range,
    assetId,
    getTileUrl,
    fetchHeaders = {},
    concurrency = 8,
    tmsTileY = false,
  } = opts;
  const { minTx, maxTx, minTy, maxTy } = range;
  const cols = maxTx - minTx + 1;
  const rows = maxTy - minTy + 1;
  const canvas = document.createElement('canvas');
  canvas.width = cols * 256;
  canvas.height = rows * 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const tasks = [];
  for (let ty = minTy; ty <= maxTy; ty++) {
    for (let tx = minTx; tx <= maxTx; tx++) {
      tasks.push({ tx, ty });
    }
  }

  {
    const nTiles = 2 ** z;
    const exTy = minTy;
    const exTx = minTx;
    const exYUrl = tmsTileY ? nTiles - 1 - exTy : exTy;
    // eslint-disable-next-line no-console
    console.groupCollapsed('[rasterOrthoTiles] XYZ stitch — fetch phase');
    // eslint-disable-next-line no-console
    console.info('assetId', assetId, 'z', z, 'tmsTileY', tmsTileY, 'slippy ty in layout → y in URL:', {
      formula: tmsTileY ? 'yUrl = (2^z - 1) - ty' : 'yUrl = ty (XYZ)',
    });
    // eslint-disable-next-line no-console
    console.info('tile index range (slippy)', { minTx, maxTx, minTy, maxTy, cols, rows, tileCount: tasks.length });
    // eslint-disable-next-line no-console
    console.info('example BFF tile URL (min corner)', getTileUrl(assetId, z, exTx, exYUrl));
    // eslint-disable-next-line no-console
    console.info('fetchHeaders keys', Object.keys(fetchHeaders || {}));
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  const drawAt = (tx, ty, bmp) => {
    const dx = (tx - minTx) * 256;
    const dy = (ty - minTy) * 256;
    ctx.drawImage(bmp, dx, dy);
    bmp.close?.();
  };

  const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
  let next = 0;
  let okCount = 0;
  async function worker() {
    for (;;) {
      const i = next++;
      if (i >= tasks.length) break;
      const { tx, ty } = tasks[i];
      const nTiles = 2 ** z;
      const yUrl = tmsTileY ? (nTiles - 1 - ty) : ty;
      const url = getTileUrl(assetId, z, tx, yUrl);
      if (i === 0 || i === tasks.length - 1 || i === Math.floor(tasks.length / 2)) {
        // eslint-disable-next-line no-console
        console.info('[rasterOrthoTiles] tile fetch →', url);
      }
      const res = await fetch(url, { headers: fetchHeaders });
      const text = res.ok ? '' : await res.text();
      if (!res.ok) {
        logTileFetchError(res.status, url, text);
        throw new Error(`Tile fetch failed ${res.status}`);
      }
      okCount += 1;
      const blob = await res.blob();
      const bmp = await createImageBitmap(blob);
      // eslint-disable-next-line no-console
      console.info('[rasterOrthoTiles] tile done', {
        tx,
        ty,
        yUrl,
        status: res.status,
        contentType: res.headers.get('content-type') || '',
        blobBytes: blob.size,
        bitmap: [bmp.width, bmp.height],
      });
      drawAt(tx, ty, bmp);
    }
  }

  const nWorkers = Math.max(1, Math.min(concurrency, tasks.length));
  await Promise.all(Array.from({ length: nWorkers }, () => worker()));

  const { minMx, maxMx, northMy, southMy } = stitchBoundsWebMercatorMeters(
    minTx,
    maxTx,
    minTy,
    maxTy,
    z,
  );

  {
    const t1 = typeof performance !== 'undefined' ? performance.now() : 0;
    // eslint-disable-next-line no-console
    console.groupCollapsed('[rasterOrthoTiles] XYZ stitch — canvas + Web Mercator footprint');
    // eslint-disable-next-line no-console
    console.info('stitched canvas pixels', { width: canvas.width, height: canvas.height });
    // eslint-disable-next-line no-console
    console.info(
      'stitchBoundsWebMercatorMeters (texture covers this WM AABB)',
      { minMx, maxMx, northMy, southMy, widthM: maxMx - minMx, heightM: northMy - southMy },
    );
    // eslint-disable-next-line no-console
    console.info('fetches OK', okCount, '/', tasks.length, 'elapsedMs', Math.round(t1 - t0));
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  return { canvas, minMx, maxMx, northMy, southMy };
}

/**
 * Native clipped bbox — Web Mercator AABB from **four corners only** (can underestimate true WM footprint).
 * @param {[number,number,number,number]} clipped [minX, minY, maxX, maxY] native
 * @param {number} nativeEpsg e.g. 26908
 */
export function nativeBBoxToWebMercatorBoundsCornerOnly(clipped, nativeEpsg) {
  assertFiniteNativeEpsg(nativeEpsg);
  ensureNad83UtmNorthDef(nativeEpsg);
  const [minX, minY, maxX, maxY] = clipped;
  const from = `EPSG:${nativeEpsg}`;
  const to = 'EPSG:3857';
  const corners = [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];
  let minMx = Infinity;
  let maxMx = -Infinity;
  let minMy = Infinity;
  let maxMy = -Infinity;
  for (const [x, y] of corners) {
    const [mx, my] = proj4(from, to, [x, y]);
    minMx = Math.min(minMx, mx);
    maxMx = Math.max(maxMx, mx);
    minMy = Math.min(minMy, my);
    maxMy = Math.max(maxMy, my);
  }
  return { minMx, maxMx, minMy, maxMy };
}

/**
 * Native axis-aligned bbox → Web Mercator AABB for **tile index pick** (not for per-vertex UV).
 * Samples each edge of the native rectangle so extrema along UTM→3857 edges are included (corners alone can miss tiles).
 * @param {[number,number,number,number]} clipped [minX, minY, maxX, maxY] native
 * @param {number} nativeEpsg
 * @param {{ samplesPerEdge?: number }} [options]
 */
export function nativeBBoxToWebMercatorBounds(clipped, nativeEpsg, options = {}) {
  const corner = nativeBBoxToWebMercatorBoundsCornerOnly(clipped, nativeEpsg);
  assertFiniteNativeEpsg(nativeEpsg);
  ensureNad83UtmNorthDef(nativeEpsg);
  const [minX, minY, maxX, maxY] = clipped;
  const from = `EPSG:${nativeEpsg}`;
  const to = 'EPSG:3857';
  const steps = Math.max(4, Math.min(64, options.samplesPerEdge ?? 24));

  let minMx = corner.minMx;
  let maxMx = corner.maxMx;
  let minMy = corner.minMy;
  let maxMy = corner.maxMy;

  const expand = (x, y) => {
    const [mx, my] = proj4(from, to, [x, y]);
    minMx = Math.min(minMx, mx);
    maxMx = Math.max(maxMx, mx);
    minMy = Math.min(minMy, my);
    maxMy = Math.max(maxMy, my);
  };

  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    expand(minX + t * (maxX - minX), minY);
    expand(maxX, minY + t * (maxY - minY));
    expand(maxX - t * (maxX - minX), maxY);
    expand(minX, maxY - t * (maxY - minY));
  }
  expand((minX + maxX) / 2, (minY + maxY) / 2);

  return { minMx, maxMx, minMy, maxMy };
}

/**
 * UV for vertex at (east, north) native given stitch bounds in Web Mercator (tile stitch extent).
 * Returns u,v in [0,1]: west→east, and **north≈0, south≈1** (matches canvas row order: north at top).
 * XYZ draping uses CanvasTexture.flipY=true and stores mesh **v** (not 1−v) so N–S aligns with DEM after RasterTileApi tile orientation.
 */
export function uvFromNativeEn(
  east,
  north,
  nativeEpsg,
  minMx,
  maxMx,
  northMy,
  southMy,
) {
  assertFiniteNativeEpsg(nativeEpsg);
  ensureNad83UtmNorthDef(nativeEpsg);
  const [mx, my] = proj4(`EPSG:${nativeEpsg}`, 'EPSG:3857', [east, north]);
  const u = (mx - minMx) / Math.max(1e-9, maxMx - minMx);
  const v = (northMy - my) / Math.max(1e-9, northMy - southMy);
  return {
    u: Math.max(0, Math.min(1, u)),
    v: Math.max(0, Math.min(1, v)),
  };
}

/**
 * Log DEM grid corners → native → Web Mercator → stitch UV → Three.js uv (u, v) for XYZ CanvasTexture.flipY=true.
 */
export function logXyzOrthoUvAlignmentProbe({
  nativeEpsg,
  minX,
  minY,
  maxX,
  maxY,
  sx,
  sy,
  minMx,
  maxMx,
  northMy,
  southMy,
}) {
  const pts = [
    { label: 'mesh NW (j=0,i=0)', j: 0, i: 0 },
    { label: 'mesh NE (j=0,i=sx-1)', j: 0, i: Math.max(0, sx - 1) },
    { label: 'mesh SW (j=sy-1,i=0)', j: Math.max(0, sy - 1), i: 0 },
    { label: 'mesh SE (j=sy-1,i=sx-1)', j: Math.max(0, sy - 1), i: Math.max(0, sx - 1) },
    { label: 'mesh center', j: Math.floor((sy - 1) / 2), i: Math.floor((sx - 1) / 2) },
  ];
  // eslint-disable-next-line no-console
  console.groupCollapsed('[rasterOrthoTiles] XYZ UV alignment (native EPSG:' + nativeEpsg + ' → 3857 → stitch)');
  // eslint-disable-next-line no-console
  console.info('DEM clipped native bbox [minX,minY,maxX,maxY]', [minX, minY, maxX, maxY]);
  // eslint-disable-next-line no-console
  console.info('terrain grid', { sx, sy });
  // eslint-disable-next-line no-console
  console.info('stitch WM bounds', { minMx, maxMx, northMy, southMy });
  for (const { label, j, i } of pts) {
    const te = sx > 1 ? i / (sx - 1) : 0;
    const tn = sy > 1 ? j / (sy - 1) : 0;
    const east = minX + te * (maxX - minX);
    const north = maxY - tn * (maxY - minY);
    const [mx, my] = proj4(`EPSG:${nativeEpsg}`, 'EPSG:3857', [east, north]);
    const { u, v } = uvFromNativeEn(east, north, nativeEpsg, minMx, maxMx, northMy, southMy);
    const uvThreeU = u;
    const uvThreeV = v;
    // eslint-disable-next-line no-console
    console.info(label, {
      gridIJ: [i, j],
      te,
      tn,
      nativeEN: { east, north },
      webMercator: { mx, my },
      stitchUV: { u, v },
      threeUv_mesh: { u: uvThreeU, v: uvThreeV },
    });
  }
  // eslint-disable-next-line no-console
  console.groupEnd();
}
